import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AvailabilityByDayType, EventsByResourceType, EventsListType, SchedulerDraggableId, SchedulerDraggingPlaceholder, SchedulerDroppableId, SchedulingView } from '../SchedulingTypes';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { DragDropEvent, useDragDropContext } from 'services/dragdrop/DragDropContext';
import { determineProjectSchedulerFetchParams } from '../functions/schedulingDateUtils';
import { useFetchData } from 'services/api/useFetchData';
import ProjectSchedulerResources from './ProjectSchedulerResources';
import { generateIndividualJobResources, generateProjectResources } from './functions/generateProjectResources';
import { generateProjectEvents } from './functions/generateProjectEvents';
import { handleAvailableEmployeeDrag } from './functions/handleAvailableEmployeeDrag';
import { handleUnassignEmployeeDrag } from './functions/handleUnassignEmployeeDrag';
import { handleDragBetweenProjects } from './functions/handleDragBetweenProjects';
import { useTranslation } from 'react-i18next';

interface ProjectSchedulerProps {
    view: SchedulingView;
    days: Date[];
    hours: string[] | undefined;
    timeSlots: string[] | undefined;
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId> | null;
    refreshTrigger: number;
    setLoading: (loading: string) => void;
}

const ProjectScheduler: React.FC<ProjectSchedulerProps> = ({
    view, days, hours, timeSlots, dragResult, refreshTrigger, setLoading
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone } = useSettings();
    const { dragging, isDragging, dragOver } = useDragDropContext<SchedulerDraggableId, SchedulerDroppableId>();
    const [draggingPlaceholder, setDraggingPlaceholder] = useState<SchedulerDraggingPlaceholder | null>(null);
    const [projectJobs, setProjectJobs] = useState<any>([]);
    const [individualJobs, setIndividualJobs] = useState<any>([])
    const [events, setEvents] = useState<EventsListType>({});
    const [eventsByResource, setEventsByResource] = useState<EventsByResourceType>({});
    const [availabilityByDay, setAvailabilityByDay] = useState<AvailabilityByDayType>({});
    const processedDragRef = useRef<string | null>(null);

    // Update the event fetch params
    const eventFetchParams = useMemo(() => {
        if (!days || days.length === 0) return {};
        return determineProjectSchedulerFetchParams(days);
    }, [JSON.stringify(days)]);

    // Fetch the events
    const { response, loading: projectEventsLoading, refetch } = useFetchData({
        apiUrl: Object.keys(eventFetchParams).length > 0 ? "get_project_scheduler_events" : "",
        params: eventFetchParams
    });

    // Update the loading state
    useEffect(() => {
        setLoading(projectEventsLoading);
    }, [projectEventsLoading]);

    // Refresh the events on trigger
    useEffect(() => {
        refetch();
    }, [refreshTrigger]);

    // Set the resources and events
    useEffect(() => {
        if (response) {
            // Generate the project job resources
            const projectJobResources = generateProjectResources(response.projects);
            setProjectJobs(projectJobResources);

            // Generate the individual job resources
            const individualJobResources = generateIndividualJobResources(response.individual_jobs);
            setIndividualJobs(individualJobResources);

            // Generate the events
            const { generatedEvents, generatedEventsByResource, generatedAvailabilityByDay } = generateProjectEvents(
                response, userLocale, userTimezone, days
            )

            // Set the events
            setEvents(generatedEvents)
            setEventsByResource(generatedEventsByResource)
            setAvailabilityByDay(generatedAvailabilityByDay)
        }
    }, [response]);

    useEffect(() => {
        if (dragResult) {
            // Create a drag key to prevent duplicated handling
            const dragKey = JSON.stringify(dragResult);
            if (processedDragRef.current === dragKey) return;

            // Set the new drag key
            processedDragRef.current = dragKey;

            // Handle the drag result
            const handleDragResult = async () => {

                // Return if required values are missing
                if (!dragResult?.draggableId ||
                    !dragResult?.source?.droppableId ||
                    !dragResult?.destination?.droppableId
                ) {
                    return;
                }

                // Get the route of the drag
                const route = {
                    draggable: dragResult.draggableId.type,
                    source: dragResult.source.droppableId?.type,
                    destination: dragResult.destination?.droppableId.type
                }

                // Handle available employee drag
                if (route.draggable === "availableEmployee") {
                    handleAvailableEmployeeDrag(
                        dragResult, 
                        userLocale, 
                        userTimezone,
                        setFloatingAlert, 
                        setEvents, 
                        setEventsByResource,
                        setAvailabilityByDay
                    );
                }

                // Handle unassign employee drag
                else if (
                    (route.draggable === "projectJobEmployeeEvent" || route.draggable === "individualJobEmployeeEvent") &&
                    route.destination === "availableEmployeesRow"
                ) {
                    handleUnassignEmployeeDrag(
                        dragResult,
                        setFloatingAlert, 
                        setEvents, 
                        setEventsByResource,
                        setAvailabilityByDay
                    )
                }

                // Handle drag between project jobs
                else if (route.draggable === "projectJobEmployeeEvent" && route.destination === "projectJob") {
                    handleDragBetweenProjects(
                        dragResult,
                        userLocale, 
                        userTimezone,
                        setFloatingAlert, 
                        setEvents, 
                        setEventsByResource,
                    )
                }

                // Show alert when dragging between individual jobs
                else if (route.draggable === "individualJobEmployeeEvent" && route.destination === "individualJob") {
                    setFloatingAlert({
                        type: "warning",
                        message: t("scheduling.scheduling_board.cannot_drag_between_individual_jobs")
                    });
                    return;
                }

                // Show alert when dragging from project job to individual job
                else if (route.draggable === "projectJobEmployeeEvent" && route.destination === "individualJob") {
                    setFloatingAlert({
                        type: "warning",
                        message: t("scheduling.scheduling_board.cannot_drag_between_project_jobs_individual_jobs")
                    });
                    return;
                }

                // Show alert when dragging from an individual job to a project job
                else if (route.draggable === "individualJobEmployeeEvent" && route.destination === "projectJob") {
                    setFloatingAlert({
                        type: "warning",
                        message: t("scheduling.scheduling_board.cannot_drag_between_individual_jobs_project_jobs")
                    });
                    return;
                }
            }
            handleDragResult();
        }
    }, [dragResult]);

    // Set the dragging placeholder
    useEffect(() => {
        // Reset dragging placeholder
        if (!isDragging || !dragOver || !dragging || !dragging.origin.droppableId) {
            setDraggingPlaceholder(null);
            return;
        }

        // Get the source day, dragover day and source type
        const sourceDay = "day" in dragging.origin.droppableId ? dragging.origin.droppableId.day : undefined
        const dragOverDay = "day" in dragOver ? dragOver.day : undefined
        const sourceType = dragging.draggableId?.type

        // Don't show a placeholder when dragging over a different day
        if (sourceDay !== dragOverDay) {
            setDraggingPlaceholder(null)
            return
        }

        // Don't show a placeholder when dragging inside the available employees row
        if (sourceType === 'availableEmployee' && dragOver.type === 'availableEmployeesRow') {
            setDraggingPlaceholder(null)
            return
        }

        // Show dragging placeholder on list columns
        if (dragOver.type === 'availableEmployeesRow' || dragOver.type === 'projectJob') {
            const { resourceId, day } = dragOver;
            setDraggingPlaceholder({ resourceId, day });
            return
        }
    
        // Show dragging placeholder on timeslot columns
        if (dragOver.type === 'individualJob') {
            const { resourceId, day, timeSlot } = dragOver;
            setDraggingPlaceholder({ resourceId, day, timeSlot, durationMinutes: 60 });
            return
        }        
    }, [isDragging, dragOver, dragging]);

    return (
        <div className={`scheduling-board stacked ${view.viewType} grid-${days.length}-columns`}>

            {/* Render resources */}
            <ProjectSchedulerResources
                view={view}
                days={days}
                hours={hours}
                timeSlots={timeSlots}
                isDragging={isDragging}
                draggingPlaceholder={draggingPlaceholder}
                projectJobs={projectJobs}
                individualJobs={individualJobs}
                events={events}
                eventsByResource={eventsByResource}
                availabilityByDay={availabilityByDay}
            />
        </div>
    )
}

export default ProjectScheduler;